import { graphql } from "gatsby"
import React from "react"
import {
  ContentfulSpacer as Spacer,
  CONTENTFUL_SPACER_TYPENAME,
  IContentfulSpacer,
} from "../atoms/layout-primitives/spacer"
import FeaturedArticles, {
  FEATURED_ARTICLES_TYPENAME,
  IFeaturedArticles,
} from "../molecules/featured-articles"
import Divider, { DIVIDER_TYPENAME, IDivider } from "../organisms/divider"
import {
  ContentfulFAQs,
  FAQ_TYPENAME,
  IContentfulFAQs,
} from "../organisms/faq-section"
import {
  ContentfulFeatureGrid as FeatureGrid,
  FEATURE_GRID_TYPENAME,
  IContentfulFeatureGrid,
} from "../organisms/feature-grid/feature-grid"
import FullWidthContent, {
  FULL_WIDTH_CONTENT_TYPENAME,
  IFullWidthContent,
} from "../organisms/full-width-content"
import IndustryList, {
  IIndustryList,
  INDUSTRY_LIST_TYPENAME,
} from "../organisms/industry-list"
import LogoGrid, { ILogoGrid, LOGO_GRID_TYPENAME } from "../organisms/logo-grid"
import SideBySideContent, {
  ISideBySideContent,
  SIDE_BY_SIDE_CONTENT_TYPENAME,
} from "../organisms/side-by-side-content"
import TestimonialCardSlider, {
  ITestimonialCardSlider,
  TESTIMONIAL_SLIDER_TYPENAME,
} from "../organisms/testimonial-card-slider"
import UsecaseCards, {
  IUseCaseCards,
  USE_CASE_CARDS_TYPENAME,
} from "../organisms/usecase-cards"
import { TWebsite } from "../types/contentful.types"

type TComponent =
  | IFeaturedArticles
  | ISideBySideContent
  | IContentfulSpacer
  | IDivider
  | IContentfulFeatureGrid
  | IContentfulFAQs
  | IFullWidthContent
  | ITestimonialCardSlider
  | ILogoGrid
  | IIndustryList
  | IUseCaseCards
interface ISectionsProps {
  components: TComponent[]
  website: TWebsite
}

/** Renders a component based on Contentful __typename */
const Sections = ({ components, website }: ISectionsProps) => (
  <>
    {Array.isArray(components)
      ? components.map((pageComponent, index) => {
          switch (pageComponent.__typename) {
            case FEATURED_ARTICLES_TYPENAME:
              return (
                <FeaturedArticles
                  key={index}
                  {...pageComponent}
                  website={website}
                />
              )
            case SIDE_BY_SIDE_CONTENT_TYPENAME:
              return <SideBySideContent key={index} {...pageComponent} />
            case CONTENTFUL_SPACER_TYPENAME:
              return <Spacer key={index} {...pageComponent} />
            case DIVIDER_TYPENAME:
              return <Divider key={index} {...pageComponent} />
            case FEATURE_GRID_TYPENAME:
              return <FeatureGrid key={index} {...pageComponent} />
            case FAQ_TYPENAME:
              return <ContentfulFAQs key={index} {...pageComponent} />
            case FULL_WIDTH_CONTENT_TYPENAME:
              return <FullWidthContent key={index} {...pageComponent} />
            case TESTIMONIAL_SLIDER_TYPENAME:
              return <TestimonialCardSlider key={index} {...pageComponent} />
            case LOGO_GRID_TYPENAME:
              return <LogoGrid key={index} {...pageComponent} />
            case INDUSTRY_LIST_TYPENAME:
              return <IndustryList key={index} {...pageComponent} />
            case USE_CASE_CARDS_TYPENAME:
              return <UsecaseCards key={index} {...pageComponent} />
            default:
              return null
          }
        })
      : null}
  </>
)

/** Graphql Fragment which makes all components available with necessary attributes */
export const SectionsFragment = graphql`
  fragment Sections on ContentfulWebsitePage {
    components {
      __typename
      ... on ContentfulFeaturedCards {
        ...ContentfulFeaturedArticles
      }
      ... on ContentfulSideBySideContent {
        ...ContentfulSideBySideContent
      }
      ... on ContentfulSpacer {
        ...ContentfulSpacer
      }
      ... on ContentfulDivider {
        ...ContentfulDivider
      }
      ... on ContentfulFeatureGrid {
        ...ContentfulFeatureGrid
      }
      ... on ContentfulFrequentlyAskedQuestions {
        ...ContentfulFrequentlyAskedQuestions
      }
      ... on ContentfulFullWidthContent {
        ...ContentfulFullWidthContent
      }
      ... on ContentfulTestimonialsBlock {
        ...ContentfulTestimonialsBlock
      }
      ... on ContentfulLogoGrid {
        ...ContentfulLogoGrid
      }
      ... on ContentfulIndustryList {
        ...ContentfulIndustryList
      }
      ... on ContentfulUseCaseList {
        ...ContentfulUseCaseList
      }
    }
  }
`

export default Sections
